import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useNotifications } from "../contexts/NotificationContext";

export default function EditSitePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteData, setSiteData] = useState(null);
  
  // Form states
  const [form, setForm] = useState({
    niche: "", service: "", city: "", zipcode: "", state_name: "", state_code: "",
    phone: "", email: "", favicon_url: "", related_services: "", search_console_code: "",
  });
  
  const [aiContent, setAiContent] = useState({
    meta_title: "", meta_desc: "", keywords: "", hero_title: "", hero_desc: "",
    homepage_main_content: "", homepage_faq: [], services: [], reviews: []
  });
  
  const [saveMode, setSaveMode] = useState("simple");
  const [validation, setValidation] = useState({});
  
  const { success, error, loading: showLoading, updateNotification } = useNotifications();

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      name: 'Basic Info',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      description: 'Business details and contact info'
    },
    {
      id: 'seo',
      name: 'SEO & Meta',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      description: 'SEO settings and meta tags'
    },
    {
      id: 'content',
      name: 'Content',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      description: 'Hero section and homepage content'
    },
    {
      id: 'services',
      name: 'Services',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      description: 'Service pages and FAQs'
    },
    {
      id: 'reviews',
      name: 'Reviews',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ),
      description: 'Customer testimonials'
    }
  ];

  useEffect(() => {
    fetchSite();
  }, [id]);

  const fetchSite = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("sites").select("*").eq("id", id).single();
      if (error || !data) {
        error("Site not found.");
        navigate('/dashboard');
        return;
      }
      
      setSiteData(data);
      
      // Set basic form data
      setForm({
        niche: data.niche || "", service: data.service || "", city: data.city || "",
        zipcode: data.zipcode || "", state_name: data.state_name || "", 
        state_code: data.state_code || "", phone: data.phone || "", email: data.email || "",
        favicon_url: data.favicon_url || "", search_console_code: data.search_console_code || "",
        related_services: Array.isArray(data.related_services) 
          ? data.related_services.join("\n") : data.related_services || "",
      });

      // Set AI content data
      if (data.ai_content) {
        setAiContent({
          meta_title: data.ai_content.meta_title || "",
          meta_desc: data.ai_content.meta_desc || "",
          keywords: data.ai_content.keywords || "",
          hero_title: data.ai_content.hero_title || "",
          hero_desc: data.ai_content.hero_desc || "",
          homepage_main_content: data.ai_content.homepage_main_content || "",
          homepage_faq: data.ai_content.homepage_faq || [],
          services: data.ai_content.services || [],
          reviews: data.ai_content.reviews || []
        });
      }
    } catch (err) {
      error("Failed to load site data: " + err.message);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'state_code' ? value.toUpperCase() : value;
    setForm(prev => ({ ...prev, [name]: finalValue }));
    
    // Clear validation error
    if (validation[name]) {
      setValidation(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleAiContentChange = (e) => {
    const { name, value } = e.target;
    setAiContent(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!form.niche.trim()) errors.niche = "Niche is required";
    if (!form.service.trim()) errors.service = "Service is required";
    if (!form.city.trim()) errors.city = "City is required";
    if (!form.zipcode.trim()) errors.zipcode = "Zipcode is required";
    if (!form.state_name.trim()) errors.state_name = "State name is required";
    if (!form.state_code.trim()) {
      errors.state_code = "State code is required";
    } else if (!/^[A-Z]{2}$/.test(form.state_code)) {
      errors.state_code = "State code must be exactly two UPPERCASE letters (e.g. TX, AZ)";
    }
    if (!form.phone.trim()) errors.phone = "Phone number is required";
    if (!form.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setActiveTab('basic'); // Switch to basic tab if validation fails
      return;
    }
    
    setSaving(true);
    const loadingId = showLoading(saveMode === "regenerate" ? "Saving and regenerating..." : "Saving changes...");
    
    try {
      const relatedServicesArray = form.related_services.split("\n").map(s => s.trim()).filter(Boolean);
      
      const updateData = {
        ...form,
        related_services: relatedServicesArray,
        ai_content: aiContent,
        updated_at: new Date().toISOString(),
      };

      if (saveMode === "regenerate") {
        updateData.status = "working";
      }

      const { error } = await supabase.from("sites").update(updateData).eq("id", id);
        
      if (error) throw error;
      
      updateNotification(loadingId, {
        type: 'success',
        message: saveMode === "regenerate" ? 'Site updated and regeneration started!' : 'Site information saved successfully!',
        duration: 5000
      });
      
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      updateNotification(loadingId, {
        type: 'error',
        message: 'Save failed: ' + err.message,
        duration: 7000
      });
    } finally {
      setSaving(false);
    }
  };

  // FAQ Management Functions
  const updateHomepageFaq = (index, field, value) => {
    const newFaqs = [...aiContent.homepage_faq];
    newFaqs[index] = { ...newFaqs[index], [field]: value };
    setAiContent({ ...aiContent, homepage_faq: newFaqs });
  };

  const addHomepageFaq = () => {
    setAiContent({
      ...aiContent,
      homepage_faq: [...aiContent.homepage_faq, { q: "", a: "" }]
    });
  };

  const removeHomepageFaq = (index) => {
    const newFaqs = aiContent.homepage_faq.filter((_, i) => i !== index);
    setAiContent({ ...aiContent, homepage_faq: newFaqs });
  };

  // Service Management Functions
  const updateService = (serviceIndex, field, value) => {
    const newServices = [...aiContent.services];
    newServices[serviceIndex] = { ...newServices[serviceIndex], [field]: value };
    setAiContent({ ...aiContent, services: newServices });
  };

  const updateServiceFaq = (serviceIndex, faqIndex, field, value) => {
    const newServices = [...aiContent.services];
    const newFaqs = [...(newServices[serviceIndex].faqs || [])];
    newFaqs[faqIndex] = { ...newFaqs[faqIndex], [field]: value };
    newServices[serviceIndex] = { ...newServices[serviceIndex], faqs: newFaqs };
    setAiContent({ ...aiContent, services: newServices });
  };

  const addServiceFaq = (serviceIndex) => {
    const newServices = [...aiContent.services];
    if (!newServices[serviceIndex].faqs) {
      newServices[serviceIndex].faqs = [];
    }
    newServices[serviceIndex].faqs = [...newServices[serviceIndex].faqs, { q: "", a: "" }];
    setAiContent({ ...aiContent, services: newServices });
  };

  const removeServiceFaq = (serviceIndex, faqIndex) => {
    const newServices = [...aiContent.services];
    newServices[serviceIndex].faqs = (newServices[serviceIndex].faqs || []).filter((_, i) => i !== faqIndex);
    setAiContent({ ...aiContent, services: newServices });
  };

  const addService = () => {
    const newService = {
      service: "",
      service_des: "",
      service_main_content: "",
      faqs: []
    };
    setAiContent({ ...aiContent, services: [...aiContent.services, newService] });
  };

  const removeService = (serviceIndex) => {
    const newServices = aiContent.services.filter((_, i) => i !== serviceIndex);
    setAiContent({ ...aiContent, services: newServices });
  };

  // Review Management Functions
  const updateReview = (reviewIndex, field, value) => {
    const newReviews = [...aiContent.reviews];
    newReviews[reviewIndex] = { ...newReviews[reviewIndex], [field]: value };
    setAiContent({ ...aiContent, reviews: newReviews });
  };

  const addReview = () => {
    const newReview = { name: "", review_text: "" };
    setAiContent({ ...aiContent, reviews: [...aiContent.reviews, newReview] });
  };

  const removeReview = (reviewIndex) => {
    const newReviews = aiContent.reviews.filter((_, i) => i !== reviewIndex);
    setAiContent({ ...aiContent, reviews: newReviews });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Niche <span className="text-red-500">*</span>
          </label>
          <input
            name="niche"
            value={form.niche}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              validation.niche ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="e.g. Home Services, Healthcare"
          />
          {validation.niche && <p className="text-red-500 text-sm mt-1">{validation.niche}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Service <span className="text-red-500">*</span>
          </label>
          <input
            name="service"
            value={form.service}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              validation.service ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="e.g. Electrician, Plumber"
          />
          {validation.service && <p className="text-red-500 text-sm mt-1">{validation.service}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City <span className="text-red-500">*</span>
          </label>
          <input
            name="city"
            value={form.city}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              validation.city ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="e.g. Phoenix"
          />
          {validation.city && <p className="text-red-500 text-sm mt-1">{validation.city}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ZIP Code <span className="text-red-500">*</span>
          </label>
          <input
            name="zipcode"
            value={form.zipcode}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              validation.zipcode ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="e.g. 85001"
          />
          {validation.zipcode && <p className="text-red-500 text-sm mt-1">{validation.zipcode}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State Name <span className="text-red-500">*</span>
          </label>
          <input
            name="state_name"
            value={form.state_name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              validation.state_name ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="e.g. Arizona"
          />
          {validation.state_name && <p className="text-red-500 text-sm mt-1">{validation.state_name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State Code <span className="text-red-500">*</span>
          </label>
          <input
            name="state_code"
            value={form.state_code}
            onChange={handleChange}
            maxLength={2}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              validation.state_code ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="AZ"
          />
          {validation.state_code && <p className="text-red-500 text-sm mt-1">{validation.state_code}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Phone <span className="text-red-500">*</span>
          </label>
          <input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              validation.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="(555) 123-4567"
          />
          {validation.phone && <p className="text-red-500 text-sm mt-1">{validation.phone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Email <span className="text-red-500">*</span>
          </label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              validation.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="contact@yourbusiness.com"
          />
          {validation.email && <p className="text-red-500 text-sm mt-1">{validation.email}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Related Services
        </label>
        <textarea
          name="related_services"
          value={form.related_services}
          onChange={handleChange}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="List each service on a new line..."
        />
        <p className="text-xs text-gray-500 mt-1">Each service on a new line</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Favicon URL
          </label>
          <input
            name="favicon_url"
            type="url"
            value={form.favicon_url}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="https://example.com/favicon.ico"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Search Console Code
          </label>
          <input
            name="search_console_code"
            value={form.search_console_code}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="google-site-verification=..."
          />
        </div>
      </div>
    </div>
  );

  const renderSeoMeta = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Title
        </label>
        <input
          name="meta_title"
          value={aiContent.meta_title}
          onChange={handleAiContentChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="SEO-optimized page title"
        />
        <p className="text-xs text-gray-500 mt-1">Recommended: 50-60 characters</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Description
        </label>
        <textarea
          name="meta_desc"
          value={aiContent.meta_desc}
          onChange={handleAiContentChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Brief description for search engines"
        />
        <p className="text-xs text-gray-500 mt-1">Recommended: 150-160 characters</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Keywords
        </label>
        <input
          name="keywords"
          value={aiContent.keywords}
          onChange={handleAiContentChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="keyword1, keyword2, keyword3"
        />
        <p className="text-xs text-gray-500 mt-1">Comma-separated list of target keywords</p>
      </div>
    </div>
  );

  const renderContent = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Hero Title
        </label>
        <input
          name="hero_title"
          value={aiContent.hero_title}
          onChange={handleAiContentChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Main headline for your homepage"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Hero Description
        </label>
        <textarea
          name="hero_desc"
          value={aiContent.hero_desc}
          onChange={handleAiContentChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Supporting text under your main headline"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Homepage Main Content
        </label>
        <textarea
          name="homepage_main_content"
          value={aiContent.homepage_main_content}
          onChange={handleAiContentChange}
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Main content for your homepage (supports Markdown)"
        />
        <p className="text-xs text-gray-500 mt-1">Supports Markdown formatting</p>
      </div>

      {/* Homepage FAQs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Homepage FAQs
          </label>
          <button
            type="button"
            onClick={addHomepageFaq}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
          >
            Add FAQ
          </button>
        </div>
        
        {aiContent.homepage_faq.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-500 mb-2">No FAQs added yet</p>
            <button
              type="button"
              onClick={addHomepageFaq}
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Add your first FAQ
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {aiContent.homepage_faq.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm text-gray-700">FAQ {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeHomepageFaq(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
                <div className="space-y-2">
                  <input
                    placeholder="Question"
                    value={faq.q || ""}
                    onChange={(e) => updateHomepageFaq(index, 'q', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <textarea
                    placeholder="Answer"
                    value={faq.a || ""}
                    onChange={(e) => updateHomepageFaq(index, 'a', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderServices = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Service Pages</h3>
          <p className="text-sm text-gray-500">Manage individual service pages and their content</p>
        </div>
        <button
          type="button"
          onClick={addService}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Add Service
        </button>
      </div>

      {aiContent.services.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No services yet</h3>
          <p className="text-gray-500 mb-4">Add service pages to showcase what you offer</p>
          <button
            type="button"
            onClick={addService}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Add Your First Service
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {aiContent.services.map((service, serviceIndex) => (
            <div key={serviceIndex} className="border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-gray-900">Service {serviceIndex + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeService(serviceIndex)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Remove Service
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                  <input
                    placeholder="e.g. Electrical Repairs"
                    value={service.service || ""}
                    onChange={(e) => updateService(serviceIndex, 'service', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Description</label>
                  <textarea
                    placeholder="Brief description of this service..."
                    value={service.service_des || ""}
                    onChange={(e) => updateService(serviceIndex, 'service_des', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Content (Markdown)</label>
                  <textarea
                    placeholder="## Service Details&#10;&#10;Detailed content about this service..."
                    value={service.service_main_content || ""}
                    onChange={(e) => updateService(serviceIndex, 'service_main_content', e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Supports Markdown formatting</p>
                </div>

                {/* Service FAQs */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Service FAQs
                    </label>
                    <button
                      type="button"
                      onClick={() => addServiceFaq(serviceIndex)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                    >
                      Add FAQ
                    </button>
                  </div>
                  
                  {(!service.faqs || service.faqs.length === 0) ? (
                    <div className="text-center py-6 bg-white rounded border-2 border-dashed border-gray-200">
                      <p className="text-gray-500 text-sm mb-2">No FAQs for this service yet</p>
                      <button
                        type="button"
                        onClick={() => addServiceFaq(serviceIndex)}
                        className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                      >
                        Add FAQ for {service.service || 'this service'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {service.faqs.map((faq, faqIndex) => (
                        <div key={faqIndex} className="bg-white border border-gray-200 rounded p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-medium text-gray-600">FAQ {faqIndex + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeServiceFaq(serviceIndex, faqIndex)}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="space-y-2">
                            <input
                              placeholder="Question"
                              value={faq.q || ""}
                              onChange={(e) => updateServiceFaq(serviceIndex, faqIndex, 'q', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <textarea
                              placeholder="Answer"
                              value={faq.a || ""}
                              onChange={(e) => updateServiceFaq(serviceIndex, faqIndex, 'a', e.target.value)}
                              rows={2}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderReviews = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Customer Reviews</h3>
          <p className="text-sm text-gray-500">Add testimonials to build trust with potential customers</p>
        </div>
        <button
          type="button"
          onClick={addReview}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Add Review
        </button>
      </div>

      {aiContent.reviews.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
          <p className="text-gray-500 mb-4">Add customer testimonials to showcase your work</p>
          <button
            type="button"
            onClick={addReview}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Add Your First Review
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {aiContent.reviews.map((review, reviewIndex) => (
            <div key={reviewIndex} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium text-sm text-gray-700">Review {reviewIndex + 1}</span>
                <button
                  type="button"
                  onClick={() => removeReview(reviewIndex)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              </div>
              <div className="space-y-3">
                <input
                  placeholder="Customer Name"
                  value={review.name || ""}
                  onChange={(e) => updateReview(reviewIndex, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <textarea
                  placeholder="Review text..."
                  value={review.review_text || ""}
                  onChange={(e) => updateReview(reviewIndex, 'review_text', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInfo();
      case 'seo':
        return renderSeoMeta();
      case 'content':
        return renderContent();
      case 'services':
        return renderServices();
      case 'reviews':
        return renderReviews();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Edit Site</h1>
          <p className="mt-2 text-gray-600">
            {siteData?.service} in {siteData?.city}, {siteData?.state_name}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back</span>
          </button>
          {siteData?.deployed_url && (
            <a
              href={siteData.deployed_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span>View Live Site</span>
            </a>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {siteData && (
        <div className={`rounded-lg p-4 ${
          siteData.status === 'deployed' 
            ? 'bg-green-50 border border-green-200' 
            : siteData.status === 'error'
              ? 'bg-red-50 border border-red-200'
              : 'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              siteData.status === 'deployed' 
                ? 'bg-green-400' 
                : siteData.status === 'error'
                  ? 'bg-red-400'
                  : 'bg-blue-400 animate-pulse'
            }`}></div>
            <span className={`text-sm font-medium ${
              siteData.status === 'deployed' 
                ? 'text-green-800' 
                : siteData.status === 'error'
                  ? 'text-red-800'
                  : 'text-blue-800'
            }`}>
              Status: {siteData.status?.charAt(0).toUpperCase() + siteData.status?.slice(1)}
            </span>
            {siteData.platform && (
              <span className="text-sm text-gray-500">
                • Platform: {siteData.platform.toUpperCase()}
              </span>
            )}
          </div>
          {siteData.error && (
            <p className="text-red-600 text-sm mt-2">
              <strong>Error:</strong> {siteData.error}
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {tabs.find(tab => tab.id === activeTab)?.name}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {tabs.find(tab => tab.id === activeTab)?.description}
              </p>
            </div>
            
            {renderTabContent()}
          </div>
        </div>

        {/* Save Options */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Save Options</h3>
          
          <div className="space-y-3 mb-6">
            <label className="flex items-start space-x-3">
              <input
                type="radio"
                name="saveMode"
                value="simple"
                checked={saveMode === "simple"}
                onChange={(e) => setSaveMode(e.target.value)}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Save Changes Only</div>
                <div className="text-sm text-gray-500">Update the database with your changes</div>
              </div>
            </label>
            
            <label className="flex items-start space-x-3">
              <input
                type="radio"
                name="saveMode"
                value="regenerate"
                checked={saveMode === "regenerate"}
                onChange={(e) => setSaveMode(e.target.value)}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Save & Regenerate Site</div>
                <div className="text-sm text-gray-500">Update database and trigger AI regeneration with new content</div>
              </div>
            </label>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              disabled={saving}
              className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{saveMode === "regenerate" ? "Saving & Regenerating..." : "Saving..."}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{saveMode === "simple" ? "Save Changes" : "Save & Regenerate"}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}